import { useCallback, useState } from 'react';
import type { StepperItemProps } from '@components/Stepper';

import { EndorsementStep } from '../types';

interface UseStepperReturn {
  currentStep: EndorsementStep;
  getStepperItemState: (visualStepIndex: number) => StepperItemProps;
  onNext: () => void;
  onPrevious: () => void;
}

interface UseStepperArgs {
  totalSteps: number;
  onClose?: () => void;
}

export function useStepper({ totalSteps, onClose }: UseStepperArgs): UseStepperReturn {
  const initialStep = totalSteps === 4 ? EndorsementStep.ParentSelection : EndorsementStep.SummaryTemplate;
  const [currentStep, setCurrentStep] = useState<EndorsementStep>(initialStep);

  const getStepperItemState = useCallback(
    (stepIndex: number): StepperItemProps => {
      // stepIndex is 1-based from Stepper component
      // Convert to 0-based and adjust for standalone mode
      const step = totalSteps === 4 ? stepIndex - 1 : stepIndex;

      if (step === currentStep) {
        return { isActive: true };
      }

      if (step < currentStep) {
        return { isCompleted: true };
      }

      return {};
    },
    [currentStep, totalSteps],
  );

  const onPrevious = useCallback(() => {
    setCurrentStep((current) => {
      if (
        current === EndorsementStep.ParentSelection ||
        (totalSteps === 3 && current === EndorsementStep.SummaryTemplate)
      ) {
        onClose?.();
        return current;
      }

      return current - 1;
    });
  }, [totalSteps, onClose]);

  const onNext = useCallback(() => {
    setCurrentStep((current) => (current < EndorsementStep.ReasonPremium ? current + 1 : current));
  }, []);

  return {
    currentStep,
    getStepperItemState,
    onNext,
    onPrevious,
  };
}
